import {
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { Container } from "@/shared/components/Container";
import { SectionTag } from "@/shared/components/SectionTag";
import { GradientHeading } from "@/shared/components/GradientHeading";
import { Reveal } from "@/shared/components/Reveal";
import { CaseShowcaseCard } from "@/shared/components/CaseShowcaseCard";
import { usePublishedCases } from "@/features/content/hooks/usePublishedCases";
import { useInView } from "@/shared/hooks/useInView";
import { useLocale, getLocalizedPath } from "@/shared/i18n/locale";
import { getHomeContent } from "@/content/home.content";
import styles from "./Cases.module.css";

/** Intervalo do carrossel automático de cases. */
const AUTO_ROTATE_MS = 3500;

/** Tempo de pausa após interação manual antes de retomar sozinho. */
const PAUSE_AFTER_CLICK_MS = 15000;

/** Próximo id na lista, em loop circular. */
function nextCaseId<T extends { id: string }>(items: T[], currentId: string) {
  const index = items.findIndex((entry) => entry.id === currentId);
  return items[(index + 1) % items.length]?.id ?? currentId;
}

function prevCaseId<T extends { id: string }>(items: T[], currentId: string) {
  const index = items.findIndex((entry) => entry.id === currentId);
  const previous = (index - 1 + items.length) % items.length;
  return items[previous]?.id ?? currentId;
}

function ChevronIcon() {
  return (
    <svg width="7" height="10" viewBox="0 0 8 12" fill="none" aria-hidden="true">
      <path d="M4.6 6L0 1.4L1.4 0L7.4 6L1.4 12L0 10.6L4.6 6Z" fill="currentColor" />
    </svg>
  );
}

export function Cases() {
  const { cases } = usePublishedCases();
  const locale = useLocale();
  const content = getHomeContent(locale);
  const [activeId, setActiveId] = useState(cases[0]?.id ?? "");
  /** Pausa a rotação automática quando o usuário interage. */
  const [paused, setPaused] = useState(false);
  /** Muda a cada interação para reiniciar a contagem dos 15s de pausa. */
  const [clickTick, setClickTick] = useState(0);
  const active = cases.find((entry) => entry.id === activeId) ?? cases[0];

  const tabsRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef({
    /** Pointer down registrado, ainda sem decidir se é clique ou drag. */
    pending: false,
    /** Já passou do limiar — está rolando a faixa. */
    active: false,
    /** Houve drag neste gesto (bloqueia o click que viria no soltar). */
    moved: false,
    startX: 0,
    startScroll: 0,
  });

  // Observa a seção: ao sair e voltar ao viewport, retomamos a rotação
  // (mesmo padrão "afterview" do Reveal). Refresh zera o estado naturalmente.
  const { ref, inView } = useInView<HTMLElement>({ once: false });

  const pauseAutoRotate = () => {
    setPaused(true);
    setClickTick((tick) => tick + 1);
  };

  const selectCase = (id: string) => {
    setActiveId(id);
    pauseAutoRotate();
  };

  const scrollTabIntoView = (id: string) => {
    const track = tabsRef.current;
    if (!track) {
      return;
    }
    const tab = track.querySelector<HTMLElement>(`[data-case-id="${id}"]`);
    if (!tab) {
      return;
    }
    const trackRect = track.getBoundingClientRect();
    const tabRect = tab.getBoundingClientRect();
    const target =
      track.scrollLeft +
      (tabRect.left - trackRect.left) -
      (track.clientWidth - tabRect.width) / 2;
    track.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  };

  useEffect(() => {
    if (!active && cases[0]) {
      setActiveId(cases[0].id);
    }
  }, [active, cases]);

  // Mantém o logo ativo visível na faixa (auto-rotate, setas, bolinhas, clique).
  useEffect(() => {
    if (!activeId) {
      return;
    }
    scrollTabIntoView(activeId);
  }, [activeId]);

  // Re-entrar no viewport destrava a rotação automática.
  useEffect(() => {
    if (inView) {
      setPaused(false);
    }
  }, [inView]);

  // Após interação, retoma a rotação sozinho passados 15s.
  useEffect(() => {
    if (!paused) {
      return;
    }
    const timer = globalThis.setTimeout(
      () => setPaused(false),
      PAUSE_AFTER_CLICK_MS,
    );
    return () => globalThis.clearTimeout(timer);
  }, [paused, clickTick]);

  // Carrossel: avança 1 case a cada 3s, em loop infinito, só com a seção
  // visível e sem pausa manual. Respeita prefers-reduced-motion.
  useEffect(() => {
    if (paused || !inView || cases.length <= 1) {
      return;
    }
    if (globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const advance = () => setActiveId((current) => nextCaseId(cases, current));
    const timer = globalThis.setInterval(advance, AUTO_ROTATE_MS);

    return () => globalThis.clearInterval(timer);
  }, [paused, inView, cases]);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    // Toque já rola nativamente via overflow-x; só pausa o auto-rotate.
    if (event.pointerType !== "mouse") {
      pauseAutoRotate();
      return;
    }
    const track = tabsRef.current;
    if (!track) {
      return;
    }
    // Não captura nem inicia scroll ainda — só depois de mover um pouco.
    // Assim um clique limpo no logo continua selecionando o case.
    dragRef.current = {
      pending: true,
      active: false,
      moved: false,
      startX: event.clientX,
      startScroll: track.scrollLeft,
    };
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag.pending && !drag.active) {
      return;
    }
    const track = tabsRef.current;
    if (!track) {
      return;
    }
    const delta = event.clientX - drag.startX;
    if (!drag.active && Math.abs(delta) > 6) {
      drag.active = true;
      drag.moved = true;
      drag.pending = false;
      track.setPointerCapture(event.pointerId);
      pauseAutoRotate();
    }
    if (drag.active) {
      track.scrollLeft = drag.startScroll - delta;
    }
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag.pending && !drag.active) {
      return;
    }
    if (drag.active) {
      tabsRef.current?.releasePointerCapture(event.pointerId);
    }
    drag.pending = false;
    drag.active = false;
    // `moved` segue true até o clickCapture engolir o click do soltar.
  };

  const handleTabsClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (dragRef.current.moved) {
      event.preventDefault();
      event.stopPropagation();
      dragRef.current.moved = false;
    }
  };

  if (!active) {
    return null;
  }

  return (
    <section id="cases" ref={ref} className={styles.cases}>
      <Container>
        <Reveal className={styles.header}>
          <SectionTag>{content.cases.tag}</SectionTag>
          <GradientHeading className={styles.title}>
            {content.cases.title}
          </GradientHeading>
          <p className={styles.subtitle}>
            {content.cases.subtitle}
          </p>
        </Reveal>

        <Reveal delay={120} className={styles.tabsBlock}>
          {cases.length > 1 && (
            <button
              type="button"
              className={`${styles.arrow} ${styles.arrowPrevious}`}
              aria-label={content.cases.previousAriaLabel}
              onClick={() => selectCase(prevCaseId(cases, activeId))}
            >
              <ChevronIcon />
            </button>
          )}
          <div
            ref={tabsRef}
            className={styles.tabs}
            role="tablist"
            aria-label={content.cases.clientListAriaLabel}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onClickCapture={handleTabsClickCapture}
          >
            {cases.map((entry) => (
              <button
                key={entry.id}
                type="button"
                role="tab"
                data-case-id={entry.id}
                aria-selected={entry.id === activeId}
                className={[
                  styles.tab,
                  entry.id === activeId ? styles.tabActive : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => selectCase(entry.id)}
              >
                <img src={entry.logo} alt={entry.client} loading="lazy" draggable={false} />
              </button>
            ))}
          </div>
          {cases.length > 1 && (
            <button
              type="button"
              className={styles.arrow}
              aria-label={content.cases.nextAriaLabel}
              onClick={() => selectCase(nextCaseId(cases, activeId))}
            >
              <ChevronIcon />
            </button>
          )}
        </Reveal>

        <Reveal delay={160}>
          <CaseShowcaseCard
            title={active.title}
            tags={active.tags}
            metrics={active.metrics}
            screenshot={active.screenshot}
            screenshotAlt={active.screenshotAlt}
            imagePosition={active.screenshotPosition}
            href={getLocalizedPath(`/cases/${active.slug}`, locale)}
          />
        </Reveal>
      </Container>
    </section>
  );
}
